"use client";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { SettingsNavGroup } from "@/types/settings-nav";

interface SettingsPanelNavProps {
  groups: SettingsNavGroup[];
  activeId: string;
  onSelect: (id: string) => void;
  errorIds?: string[];
  warningIds?: string[];
  className?: string;
}

export function SettingsPanelNav({
  groups,
  activeId,
  onSelect,
  errorIds = [],
  warningIds = [],
  className,
}: SettingsPanelNavProps) {
  const errorIdSet = new Set(errorIds);
  const warningIdSet = new Set(warningIds);

  return (
    <>
      <div
        className={cn(
          "sticky z-10 -mx-4 border-b border-iai-border bg-background px-4 py-2 sm:-mx-6 sm:px-6 lg:hidden",
          "top-[var(--agent-detail-sticky-height,0px)]"
        )}
      >
        <Select value={activeId} onValueChange={onSelect}>
          <SelectTrigger
            className={cn(
              "w-full",
              errorIdSet.has(activeId) && "border-destructive text-destructive"
            )}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {groups
              .filter((group) => group.items.length > 0)
              .map((group) => (
                <SelectGroup key={group.id}>
                {group.label ? <SelectLabel>{group.label}</SelectLabel> : null}
                {group.items.map((item) => {
                  const hasError = errorIdSet.has(item.id);
                  return (
                    <SelectItem key={item.id} value={item.id}>
                      <span
                        className={cn(
                          "flex items-center gap-2",
                          hasError && "text-destructive"
                        )}
                      >
                        <item.icon className="size-4" />
                        {item.label}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
      </div>

      <nav
        className={cn(
          "hidden w-full shrink-0 space-y-6 lg:block lg:w-56 lg:sticky lg:top-[calc(var(--agent-detail-sticky-height,8.5rem)+1.5rem)] lg:self-start",
          className
        )}
      >
        {groups
          .filter((group) => group.items.length > 0)
          .map((group) => (
            <div key={group.id} className="space-y-1">
            {group.label ? (
              <p className="px-3 text-[11px] font-bold uppercase tracking-[0.06em] text-iai-mid-grey">
                {group.label}
              </p>
            ) : null}
            {group.items.map((item) => {
              const Icon = item.icon;
              const isActive = item.id === activeId;
              const hasError = errorIdSet.has(item.id);
              const hasWarning = !hasError && warningIdSet.has(item.id);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onSelect(item.id)}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-aios-sm px-3 py-2 text-left text-sm font-medium transition-colors",
                    hasError && !isActive && "text-destructive hover:bg-red-50 hover:text-destructive dark:hover:bg-red-950/30",
                    hasError && isActive && "bg-red-50 text-destructive dark:bg-red-950/40 dark:text-red-300",
                    !hasError &&
                      isActive &&
                      "bg-iai-purple-4 text-iai-purple dark:text-iai-purple-1",
                    !hasError &&
                      !isActive &&
                      "text-iai-dark-grey hover:bg-iai-purple-4/60 hover:text-foreground"
                  )}
                >
                  <Icon className="size-4 shrink-0" />
                  <span className="flex-1">{item.label}</span>
                  {hasError ? (
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-destructive" />
                  ) : hasWarning ? (
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                  ) : null}
                </button>
              );
            })}
          </div>
        ))}
      </nav>
    </>
  );
}
